import { Disclosure } from '@headlessui/react';
import { oneLine } from 'common-tags';

import { ConditionalWrapper } from '@/hoc';
import { ChevronDown } from '@/icons';
import { Prose } from '@/molecules';

import type { FunctionComponent, ReactNode } from 'react';
import type { TypeFaqProps } from './types';

const VARIANTS = {
  black: {
    heading: 'bg-primary-900 text-typo-alternative px-4',
    arrow: '',
    body: 'px-4 bg-typo-alternative text-typo-secondary u-body u-body--xs',
  },
  white: {
    heading: 'bg-bg-secondary text-typo-primary px-4',
    arrow: 'text-secondary-500',
    body: 'px-4 bg-typo-alternative text-typo-secondary u-body u-body--xs',
  },
  gray: {
    heading: 'bg-primary-100 text-typo-primary px-4',
    arrow: '',
    body: 'px-4 bg-typo-alternative text-typo-secondary u-body u-body--xs',
  },
  transparent: {
    heading: 'text-typo-primary',
    arrow: 'text-secondary-500',
    body: '',
  },
};

const isFAQ = (children: ReactNode) =>
  // TODO: Corregir tipos
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  children?.type?.displayName === 'Faq';

/**
 * Faq
 */
export const Faq: FunctionComponent<TypeFaqProps> = ({
  question,
  headingType = 'p',
  variant = 'black',
  Icon,
  children,
  className = '',
  open,
  classNames = {},
}: TypeFaqProps) => {
  const childIsFAQ =
    isFAQ(children) ||
    (Array.isArray(children) && children.every((child) => isFAQ(child)));
  const isString = typeof children === 'string';

  return (
    <Disclosure
      as="div"
      className={oneLine`
        ${childIsFAQ ? '' : 'border-b border-primary-100'}
        [&:has([id^="headlessui-disclosure-button"])>[id^="headlessui-disclosure-panel"]_[id^="headlessui-disclosure-button"]]:u-body
        [&:has([id^="headlessui-disclosure-button"])>[id^="headlessui-disclosure-panel"]_[id^="headlessui-disclosure-button"]]:u-body--s
        [&:has([id^="headlessui-disclosure-button"])>[id^="headlessui-disclosure-panel"]_[id^="headlessui-disclosure-button"]]:normal-case
        ${className}
      `}
      defaultOpen={open}
    >
      {({ open }) => (
        <>
          <Disclosure.Button
            as={headingType}
            role="button"
            className={oneLine`
              u-headline u-headline--h4 flex w-full items-center justify-between py-2 text-left
              ${VARIANTS[variant].heading}
              ${classNames?.header ?? ''}
            `}
          >
            <ConditionalWrapper
              condition={Boolean(Icon)}
              wrapper={(children) => (
                <span className="inline-flex flex-row place-items-center gap-4">
                  {Icon && <Icon width={16} height={16} />}
                  {children}
                </span>
              )}
            >
              {question}
            </ConditionalWrapper>

            <ChevronDown
              className={oneLine`
              transition
              ${open ? '-rotate-180' : 'rotate-0'}
              ${VARIANTS[variant].arrow}
            `}
            />
          </Disclosure.Button>

          <Disclosure.Panel
            unmount={false}
            {...(!childIsFAQ && {
              className: oneLine`
                py-4 u-body u-body--xs
                ${VARIANTS[variant].body}
                ${classNames?.body ?? ''}
              `,
            })}
          >
            {isString ? (
              <Prose
                html={children}
                className="[&.prose]:u-body [&.prose]:u-body--xs [&.prose]:text-typo-secondary"
              />
            ) : (
              children
            )}
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
};

Faq.displayName = 'Faq';
